/**
 * Display labels + tone mapping for the regulatory filing workspace. Kept in one
 * place so the filings list, workspace, and package read consistently.
 */
import type {
  FieldReviewStatus,
  FieldSource,
  FilingFormType,
  FilingStatus,
} from "@/lib/api/filings";
import type { FilingSection, FormPfFundType } from "@argus/engine";

type Tone = "neutral" | "brand" | "clear" | "risk" | "breach" | "info" | "ai";

export const FORM_LABEL: Record<FilingFormType, string> = {
  form_pf: "Form PF",
  form_adv: "Form ADV",
};

export const FILING_STATUS_LABEL: Record<FilingStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  ready: "Filing-ready",
  filed: "Filed",
  withdrawn: "Withdrawn",
};

export const FILING_STATUS_TONE: Record<FilingStatus, Tone> = {
  draft: "neutral",
  in_review: "info",
  ready: "brand",
  filed: "clear",
  withdrawn: "neutral",
};

/** Ordered statuses a user can move a filing to from the workspace. */
export const FILING_STATUS_FLOW: FilingStatus[] = [
  "draft",
  "in_review",
  "ready",
  "filed",
  "withdrawn",
];

export const SOURCE_LABEL: Record<FieldSource, string> = {
  auto: "Auto",
  ai: "AI draft",
  manual: "Manual",
};

export const SOURCE_TONE: Record<FieldSource, Tone> = {
  auto: "clear",
  ai: "ai",
  manual: "neutral",
};

export const SOURCE_HINT: Record<FieldSource, string> = {
  auto: "Derived from your fund, LP, and commitment records in Argus.",
  ai: "Drafted by Claude (or the deterministic fallback) — accept or edit.",
  manual: "Argus has no basis for this value — enter it.",
};

export const REVIEW_LABEL: Record<FieldReviewStatus, string> = {
  unreviewed: "Needs review",
  accepted: "Accepted",
  edited: "Edited",
  flagged: "Flagged",
};

export const REVIEW_TONE: Record<FieldReviewStatus, Tone> = {
  unreviewed: "risk",
  accepted: "clear",
  edited: "info",
  flagged: "breach",
};

export const SECTION_LABEL: Record<FilingSection, string> = {
  // Form PF
  section_1a: "Section 1a — Adviser information",
  section_1b: "Section 1b — Per private fund",
  section_4: "Section 4 — Large private equity adviser",
  // Form ADV Part 1A
  adv_item_1: "Item 1 — Identifying information",
  adv_item_5: "Item 5 — Advisory business",
  adv_7b1: "Schedule D 7.B.1 — Per private fund",
};

export const SECTION_BLURB: Record<FilingSection, string> = {
  // Form PF
  section_1a: "Firm-level identifying information and aggregate figures (all filers).",
  section_1b: "Reported once per private fund (all filers).",
  section_4:
    "Portfolio-company reporting for advisers with ≥ $2B in private-equity regulatory AUM. Argus does not hold portfolio-company data — these are gaps to import or enter.",
  // Form ADV Part 1A
  adv_item_1: "Firm legal identity and registration identifiers.",
  adv_item_5:
    "Advisory-business figures: regulatory AUM, private-fund counts, employees, and a drafted business summary.",
  adv_7b1:
    "Private fund reporting, once per fund. Argus auto-fills identity, domicile, commitments, and owner counts; valuation and legal determinations (GAV, PFID, exemption, ownership %) are gaps to enter.",
};

/** Section render order per form. The workspace and package iterate this. */
export const SECTION_ORDER_BY_FORM: Record<FilingFormType, FilingSection[]> = {
  form_pf: ["section_1a", "section_1b", "section_4"],
  form_adv: ["adv_item_1", "adv_item_5", "adv_7b1"],
};

export const FUND_TYPE_LABEL: Record<FormPfFundType, string> = {
  hedge_fund: "Hedge fund",
  liquidity_fund: "Liquidity fund",
  private_equity: "Private equity fund",
  real_estate: "Real estate fund",
  securitized_asset: "Securitized asset fund",
  venture_capital: "Venture capital fund",
  other: "Other private fund",
};
